import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { OriginMark } from '@/components/admin/origin-mark';
import { durationLabelOrNothing } from '@/lib/duration-label';
import { isProdOrigin } from '@/lib/session-origin';
import type { ChatRoomSummary } from '@matter_extractor/shared';
import { Tooltip } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import {
  LINK_EXPIRED_LABEL,
  LINK_EXPIRED_TITLE,
  NO_TRANSCRIPT_LABEL,
  OPEN_TRANSCRIPT_LABEL,
  ROOM_COLUMNS,
  ROOM_STATE_LABEL,
  ROOM_STATE_TONE,
  createdAtLabel,
} from './entretien-labels';

/**
 * S9, rendered: every room, its state, its two figures (chat spec §12).
 *
 * A piloting instrument and NOT a dashboard. Five columns, and each of them is a
 * question an operator actually asks - which room is this, when was it struck,
 * what happened in it, how much was said, how long it ran - so there is no
 * aggregate, no chart and no ratio here. What the cycle reads is one interview
 * at a time, and this table's job is to get somebody to the right one.
 *
 * The anatomy is the journal's, deliberately: same scroller, same `<thead>`, and
 * one `StatusBadge` rather than a second pill written here. Two badge shapes
 * under one administration bar would be two vocabularies for the same kind of
 * fact. The badge now LIVES beside this file: the coupe deleted the sessions
 * table that used to own it (issue #32).
 */

export interface RoomsTableProps {
  rooms: readonly ChatRoomSummary[];
  /** Why the list is empty, worded by the screen - it alone knows if a search is armed. */
  emptyMessage: string;
}

export function RoomsTable({ rooms, emptyMessage }: RoomsTableProps) {
  return (
    <div
      data-testid="chat-rooms-table"
      /*
        `relative` on the scroller, the correction BUILD-916 made on the five
        other tables of the administration: `overflow-x` clips only descendants
        whose containing block is the scroller, so a statically positioned one
        lets the `sr-only` caption take its position at the table's full width
        and stretch the document sideways on a phone.
      */
      className="relative overflow-x-auto rounded-md border border-gray-200 bg-white"
    >
      <table className="w-full border-collapse text-left text-small">
        <caption className="sr-only">Entretiens à l’aveugle</caption>
        <thead className="border-gray-200 border-b bg-gray-50 text-gray-600">
          <tr>
            {ROOM_COLUMNS.map((column) => (
              <th
                key={column}
                scope="col"
                className="px-3 py-2 font-semibold text-caption uppercase tracking-wide"
              >
                {column}
              </th>
            ))}
            {/* The action column has no heading in the maquette, and it keeps
                none: a header over a link that says what it does would be the
                word twice. `sr-only` rather than empty, so the column is still
                announced. */}
            <th scope="col" className="px-3 py-2 font-semibold">
              <span className="sr-only">Transcript</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rooms.length === 0 ? (
            <tr>
              <td colSpan={ROOM_COLUMNS.length + 1} className="px-3 py-8 text-center text-gray-600">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rooms.map((room) => <RoomRow key={room.id} room={room} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function RoomRow({ room }: { room: ChatRoomSummary }) {
  /*
    « Jamais ouverte » is the ONE state with nothing to open, and the cell says
    so in words rather than being blank (§12). A blank there and a link there
    would look like a rendering accident; « Aucun transcript » is the finding.
  */
  const openable = room.messageCount > 0;
  return (
    <tr data-testid="chat-room-row" className="border-gray-100 border-b last:border-b-0">
      {/*
        `<th scope="row">` on the reference: it is what NAMES this row - what the
        guest quotes to ask for an erasure (§12) - so a screen reader has to
        announce it in front of every other cell of the row.

        `tabular-nums` and a little tracking, because eight symbols read aloud
        and retyped are read character by character.
      */}
      <th scope="row" className="px-3 py-3 font-normal">
        <span className="font-semibold tabular-nums tracking-wide">{room.reference}</span>
        {/* The mark « prod » of a room the synchronisation brought up from the vitrine (vitrine.md §8.2, V12, #192). */}
        {isProdOrigin(room) ? (
          <span className="ml-2">
            <OriginMark testId="chat-room-origin" />
          </span>
        ) : null}
        {room.linkExpired ? (
          <Tooltip content={LINK_EXPIRED_TITLE}>
            <span className="ml-2 whitespace-nowrap text-gray-500 text-caption">
              {LINK_EXPIRED_LABEL}
            </span>
          </Tooltip>
        ) : null}
      </th>
      <td className="whitespace-nowrap px-3 py-3 text-gray-700 tabular-nums">
        {createdAtLabel(room.createdAt)}
      </td>
      <td className="px-3 py-3">
        <StatusBadge
          tone={ROOM_STATE_TONE[room.state]}
          // A slow blink beside the word for a conversation being held RIGHT
          // NOW - the one row on this screen somebody acts on immediately.
          pulse={room.state === 'chatting'}
        >
          {ROOM_STATE_LABEL[room.state]}
        </StatusBadge>
      </td>
      <td
        className={`px-3 py-3 tabular-nums ${room.messageCount === 0 ? 'text-gray-400' : 'text-gray-700'}`}
      >
        {room.messageCount}
      </td>
      <td
        className={`whitespace-nowrap px-3 py-3 tabular-nums ${room.durationMinutes === null ? 'text-gray-400' : 'text-gray-700'}`}
      >
        {/*
          `durationLabelOrNothing` and not a local formatter: it is the module
          the journal and the dashboard already print a duration with, and it
          holds the distinction that matters here - a dash is « nobody measured
          it », « < 1 min » is « measured, and very short ». A zero would
          collapse the two.
        */}
        {durationLabelOrNothing(room.durationMinutes)}
      </td>
      <td className="px-3 py-3 text-right">
        {openable ? (
          <Link
            to="/admin/entretiens/$roomId"
            params={{ roomId: room.id }}
            className="whitespace-nowrap font-semibold text-bleu-nuit text-small underline"
          >
            {OPEN_TRANSCRIPT_LABEL}
          </Link>
        ) : (
          <span className="whitespace-nowrap text-gray-400 text-small">{NO_TRANSCRIPT_LABEL}</span>
        )}
      </td>
    </tr>
  );
}
